import { useState, useEffect } from 'react';

import { ApiManager } from '../services/ApiManager';

export interface Product {
  id: string;
  title: string;
  handle: string;
  description: string;
  descriptionHtml: string;
  vendor?: string;
  options: {
    id: string;
    name: string;
    position: number;
    values: string[];
  }[];
  media: {
    edges: {
      node: {
        id: string;
        image: {
          url: string;
        };
      };
    }[];
  };
  variants: {
    edges: {
      node: {
        id: string;
        title: string;
        price: string;
        sku: string;
        position: number;
        availableForSale: boolean;
        inventoryQuantity: number;
        image?: {
          url: string;
          altText?: string;
        };
        selectedOptions?: Array<{
          name: string;
          value: string;
        }>;
        metafields?: {
          edges: {
            node: {
              key: string;
              type: string;
              id: string;
              value: string;
              namespace: string;
            };
          }[];
        };
      };
    }[];
  };
  other_colors: any;
  metafields: {
    edges: {
      node: {
        key: string;
        type: string;
        id: string;
        value: string;
        namespace: string;
      };
    }[];
  };
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  featuredImage: {
    url: string;
    altText: string;
  } | null;
}

export const extractProductId = (gid: string | number): string => {
  if (typeof gid === 'number') {
    return gid.toString();
  }

  if (!gid) {
    console.warn('Product ID was undefined or null');
    return '';
  }

  if (!gid.includes('gid://')) {
    const parts = gid.split('/');
    const lastPart = parts[parts.length - 1];
    if (!isNaN(Number(lastPart))) {
      return lastPart;
    }
    return gid;
  }
  const parts = gid.split('/');
  return parts[parts.length - 1];
};

export const useProductList = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchProductData = async () => {
      setLoadingProducts(true);
      setError(null);

      try {
        const fetchedProducts = await ApiManager.products.getProductData('lilja');
        setProducts(fetchedProducts);
      } catch (e) {
        console.error('Error fetching product data:', e);
        setError(e instanceof Error ? e : new Error('Failed to fetch products'));
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProductData();
  }, []);

  return { products, loadingProducts, productFetchError: error };
};

export const useProductListV2 = (source: 'lilja' | 'laukkuni' | 'default' | '2' = 'lilja') => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchProductData = async () => {
    setLoadingProducts(true);
    setError(null);

    try {
      const fetchedProducts = await ApiManager.products.getProductData(source);
      setProducts(fetchedProducts);
    } catch (e) {
      console.error(`Error fetching product data from ${source}:`, e);
      setError(e instanceof Error ? e : new Error(`Failed to fetch products from ${source}`));
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchProductData();
  }, [source]);

  const refetch = async () => {
    await fetchProductData();
  };

  return {
    products,
    loadingProducts,
    productFetchError: error,
    refetch,
  };
};
